import configData from '../../config.json';

const IMG = configData.SERVER_URL_IMG || 'https://dashboard.oolalife.com/';

/** Fictional demo profile photo — static file (not under /marketing-assets route). */
export const MARKETING_PROFILE_IMAGE_URL =
    '/marketing-media/demo-profile-jordan-rivera.jpg';

/** Fictional demo data — marketing screenshots only. */
export const demoProfile = {
    firstName: 'Jordan',
    lastName: 'Rivera',
    pictureFileName: '',
    rank: 'Member',
    rankLevel: 'Level 1',
    rankLevelCode: 'L1',
    customerLevel: 'Level 1',
    enableMyCoachSection: false,
    latestAchievements: [
        { id: 1, achievementName: 'First Steps' },
        { id: 3, achievementName: '3-Day Streak' },
        { id: 5, achievementName: 'Goal Setter' },
    ],
    achievements: { badges: [], milestones: [], streaks: [] },
};

export const demoUserProgress = {
    stepsTaken: 248,
    currentStreak: 12,
    longestStreak: 21,
    goalsCompleted: 3,
};

/** Official V1 area colors (7 F's) — from production Areas seed data. */
export const V1_AREA_COLORS = {
    Fitness: '#15C184',
    Finance: '#9AC864',
    Family: '#FABE53',
    Field: '#F16636',
    Faith: '#EB0F47',
    Friends: '#700460',
    Fun: '#022B7A',
};

const oolaAreaTemplate = [
    { id: 1, name: 'Fitness', color: V1_AREA_COLORS.Fitness, score: 72 },
    { id: 2, name: 'Finance', color: V1_AREA_COLORS.Finance, score: 65 },
    { id: 3, name: 'Family', color: V1_AREA_COLORS.Family, score: 81 },
    { id: 4, name: 'Field', color: V1_AREA_COLORS.Field, score: 58 },
    { id: 5, name: 'Faith', color: V1_AREA_COLORS.Faith, score: 62 },
    { id: 6, name: 'Friends', color: V1_AREA_COLORS.Friends, score: 70 },
    { id: 7, name: 'Fun', color: V1_AREA_COLORS.Fun, score: 74 },
];

function buildOolaScoreEntry(score, completionDate, areaName) {
    const areas = oolaAreaTemplate.map((area) => ({
        ...area,
        score: Math.max(45, Math.min(95, area.score + (score % 7) - 3)),
    }));
    return { score, completionDate, areaName, areas };
}

export const demoDashboardState = {
    AllSteps: 248,
    DailySteps: 2,
    Actions: [
        {
            id: 101,
            text: 'Morning gratitude journal (3 items)',
            canBeRemoved: true,
            index: 0,
        },
        {
            id: 102,
            text: '20-minute walk outside',
            canBeRemoved: true,
            index: 1,
        },
        {
            id: 103,
            text: 'Review weekly budget goals',
            canBeRemoved: true,
            index: 2,
        },
        {
            id: null,
            text: '',
            canBeRemoved: true,
            index: 3,
            extraText: '',
        },
    ],
    userProgress: demoUserProgress,
    profile: demoProfile,
    OolaScore: [
        buildOolaScoreEntry(72, 'Mar 12 2026', 'Fitness'),
        buildOolaScoreEntry(68, 'Feb 28 2026', 'Finance'),
        buildOolaScoreEntry(81, 'Jan 15 2026', 'Family'),
    ],
};

export const demoGoals = [
    {
        id: 1,
        text: 'Run a 5K with my family by summer',
        color: V1_AREA_COLORS.Fitness,
        isOneGoal: true,
        completedByDate: '2026-08-15',
        areaName: 'Fitness',
    },
    {
        id: 2,
        text: 'Pay off credit card balance',
        color: V1_AREA_COLORS.Finance,
        isOneGoal: false,
        completedByDate: '2026-09-30',
        areaName: 'Finance',
    },
    {
        id: 3,
        text: 'Weekly family dinner — no phones',
        color: V1_AREA_COLORS.Family,
        isOneGoal: false,
        completedByDate: '2026-07-01',
        areaName: 'Family',
    },
    {
        id: 4,
        text: 'Read one personal growth book per month',
        color: V1_AREA_COLORS.Field,
        isOneGoal: false,
        completedByDate: '2026-12-01',
        areaName: 'Field',
    },
];

/**
 * My Breakthrough Experience — real V1 lesson catalog (image paths + titles from
 * production ELearningLesson seed data). Marketing screenshots only.
 */
const BREAKTHROUGH_LESSON_CATALOG = [
    {
        id: 1,
        title: 'Grow Your Fitness to Find Balance',
        imagePath: 'media/elearning/images/Fitness_low.png',
    },
    {
        id: 2,
        title: 'Grow Your Finance to Find Balance',
        imagePath: 'media/elearning/images/Finance_low.png',
    },
    {
        id: 3,
        title: 'Grow Your Family to Find Balance',
        imagePath: 'media/elearning/images/Family_low.png',
    },
    {
        id: 4,
        title: 'Grow Your Field to Find Balance',
        imagePath: 'media/elearning/images/Field_low.png',
    },
    {
        id: 5,
        title: 'Grow Your Faith to Find Balance',
        imagePath: 'media/elearning/images/Faith_low.png',
    },
    {
        id: 6,
        title: 'Grow Your Friends to Find Balance',
        imagePath: 'media/elearning/images/Friends_low.png',
    },
    {
        id: 7,
        title: 'Grow Your Fun to Find Balance',
        imagePath: 'media/elearning/images/Fun_low.png',
    },
    {
        id: 8,
        title: 'Finding Balance Through Commitment',
        imagePath: 'media/elearning/images/Committment_high.png',
    },
    {
        id: 9,
        title: 'Committing to Your OolaLife',
        imagePath: 'media/elearning/images/Commitment_low.png',
    },
    {
        id: 10,
        title: 'Taking Daily Action',
        imagePath: 'media/elearning/images/Daily_action.png',
    },
    {
        id: 11,
        title: 'The Power of Community',
        imagePath: 'media/elearning/images/Community.png',
    },
    {
        id: 12,
        title: 'Clear the Clutter',
        imagePath: 'media/elearning/images/Clear_the_clutter.png',
    },
];

/** 12-week Breakthrough board — realistic partial progress for marketing captures. */
export const demoBreakthroughModules = BREAKTHROUGH_LESSON_CATALOG.map(
    (lesson, index) => {
        const week = index + 1;
        const isCompleted = week <= 3;
        const isUnlocked = week <= 4;
        const unlockDates = [
            'Apr 7',
            'Apr 14',
            'Apr 21',
            'Apr 28',
            'May 5',
            'May 12',
            'May 19',
            'May 26',
        ];
        return {
            id: lesson.id,
            title: lesson.title,
            imagePath: lesson.imagePath,
            isUnlocked,
            isCompleted,
            unLockDateText: isUnlocked
                ? ''
                : `Unlocks ${unlockDates[week - 5] || 'Jun 2'}`,
        };
    }
);

/** Real V1 lesson Vimeo embed — Taking Daily Action (Breakthrough). */
export const MARKETING_LESSON_VIDEO_URL =
    'https://player.vimeo.com/video/545569617';

/** Real V1 lesson detail — Taking Daily Action (Watch / Read / Do). */
export const demoModuleDetail = {
    title: 'Taking Daily Action',
    caption: 'MY BREAKTHROUGH EXPERIENCE',
    unlockedDateText: 'Mar 20, 2026',
    sections: [
        {
            name: 'Watch',
            title: 'Taking Daily Action',
            contentText:
                '<p>&ldquo;Busy&rdquo; doesn&rsquo;t equal &ldquo;productive.&rdquo; Now that you&rsquo;ve created 21 goals for your life&mdash;and narrowed them down to your Top 7 Goals plus your Oola One&mdash;it&rsquo;s time to take action and bring these outcomes into being. What are some smart ways to focus on the daily steps that will get you to your goals? Watch and find out.</p>',
            videoUrl: MARKETING_LESSON_VIDEO_URL,
        },
        {
            name: 'Read',
            title: 'Read',
            introText:
                '<p>What&rsquo;s your favorite flavor? Dave&rsquo;s is lemon yellow. We&rsquo;re talking, of course, about that colorful candy, Skittles, which had a starring role in the Design My Life Blueprint alongside your most important goals: Oranges.</p>',
            contentText:
                '<p>If you think about it, our lives are a lot like those Skittles and Oranges. Unfortunately, for too many people, their days are filled with hours of busyness&mdash;but no meaningful progress toward their goals.</p>',
            imageUrl: 'media/elearning/images/Daily_action.png',
        },
        {
            name: 'Do',
            title: 'Do',
            contentText:
                '<p>Check out the exercises in this week&rsquo;s downloadable workbook to help you create new habits and accomplish small, but important, tasks daily.</p>',
        },
    ],
    questions: [],
};

/** My Path — Fuel your Passion for Fitness (open module preview). */
export const demoFuelPassionModuleDetail = {
    title: 'Fuel your Passion for Fitness',
    caption: 'MY PERSONALIZED PATH',
    unlockedDateText: 'Apr 2, 2026',
    sections: [
        {
            name: 'Watch',
            title: 'Fuel your Passion for Fitness',
            contentText:
                '<p>When fitness becomes something you love—not something you force—you stay consistent. In this module, discover how to reconnect with movement, build sustainable habits, and make fitness a lifelong source of energy for your Oola life.</p>',
            videoUrl: MARKETING_LESSON_VIDEO_URL,
        },
        {
            name: 'Read',
            title: 'Read',
            introText:
                '<p>Passion is the difference between starting and staying. Members who fuel their fitness with purpose see stronger streaks, better balance across all seven areas, and more confidence in daily action steps.</p>',
            contentText:
                '<p>Use the workbook exercises to identify your fitness “why,” design a weekly rhythm that fits your life, and choose one small action you can take today.</p>',
            imageUrl: 'media/elearning/images/FitnessHigh.png',
        },
        {
            name: 'Do',
            title: 'Do',
            contentText:
                '<p>Complete this week’s workbook prompts and add one fitness action step to your daily tracker.</p>',
        },
    ],
    questions: [],
};

export { IMG as marketingImageBase };
